import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Select, { SingleValue } from 'react-select';
import { approvalsApi, employeeApi, locationApi } from '../services/api';
import type { EmployeePickerRow } from '../services/api';
import { AuthorityDelegationRow, DelegationHistoryResult, Location } from '../types';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { formatCompanyDateTime } from '../utils/companyDateFormat';

const WORK_TYPES = [
  { id: 'Holiday', label: 'Holiday' },
  { id: 'SickLeave', label: 'Sick leave' },
  { id: 'Timecard', label: 'Timecards' },
  { id: 'Marketplace', label: 'Shift marketplace' },
  { id: 'TaskAccept', label: 'Task acceptance' },
  { id: 'Unavailability', label: 'Unavailability' },
] as const;

type ColleagueOption = { value: number; label: string };

function workTypeLabel(code: string): string {
  return WORK_TYPES.find((t) => t.id === code)?.label ?? code;
}

function formatWorkTypes(raw?: string | null): string {
  if (!raw?.trim()) return 'all approval types';
  return raw
    .split(',')
    .map((part) => workTypeLabel(part.trim()))
    .filter(Boolean)
    .join(', ');
}

function periodLabel(from: string, to?: string | null, active?: boolean): string {
  const start = formatCompanyDateTime(from);
  if (active && !to) return `${start} – current`;
  if (!to) return start;
  return `${start} – ${formatCompanyDateTime(to)}`;
}

const MyTeam: React.FC = () => {
  const { employee } = useAuth();
  const { sickLeaveEnabled } = usePermissions();
  const workTypes = WORK_TYPES.filter((type) => sickLeaveEnabled || type.id !== 'SickLeave');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const requestedAll = params.get('scope') === 'all';
  const tab = params.get('tab') === 'history' ? 'history' : 'current';
  const [delegations, setDelegations] = useState<AuthorityDelegationRow[]>([]);
  const [history, setHistory] = useState<DelegationHistoryResult | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [people, setPeople] = useState<EmployeePickerRow[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [colleague, setColleague] = useState<ColleagueOption | null>(null);
  const [allTypes, setAllTypes] = useState(true);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [dels, rows, locs] = await Promise.all([
        approvalsApi.listDelegations(),
        employeeApi.getPicker(),
        locationApi.getAll().catch(() => [] as Location[]),
      ]);
      setDelegations(dels);
      setPeople(rows);
      setLocations(locs);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load My Delegates.');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async (scope: 'mine' | 'all') => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const result = await approvalsApi.delegationHistory(scope);
      setHistory(result);
    } catch (e: any) {
      if (scope === 'all' && e?.response?.status === 403) {
        try {
          setHistory(await approvalsApi.delegationHistory('mine'));
        } catch (inner: any) {
          setHistory(null);
          const data = inner?.response?.data;
          const apiMessage = typeof data === 'string' ? data : data?.message;
          setHistoryError(apiMessage || 'Failed to load delegation history.');
        }
        return;
      }
      setHistory(null);
      const data = e?.response?.data;
      const apiMessage = typeof data === 'string' ? data : data?.message;
      setHistoryError(apiMessage || (e?.code === 'ECONNABORTED' ? 'Delegation history took too long to load.' : 'Failed to load delegation history.'));
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (tab !== 'history') return;
    void loadHistory(requestedAll ? 'all' : 'mine');
  }, [tab, requestedAll]);

  const setTab = (next: 'current' | 'history') => {
    const nextParams = new URLSearchParams(params);
    nextParams.set('tab', next);
    setParams(nextParams, { replace: true });
  };

  const locationName = useMemo(() => {
    const map = new Map<number, string>();
    for (const loc of locations) map.set(loc.id, loc.name);
    return map;
  }, [locations]);

  const colleagueOptions = useMemo(() => {
    return people
      .filter((row) => row.isActive && row.id !== employee?.id)
      .map((row) => {
        const name = `${row.firstName} ${row.lastName}`.trim();
        const site = locationName.get(row.mainLocationId);
        return { value: row.id, label: site ? `${name} · ${site}` : name };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [people, employee?.id, locationName]);

  const toggleType = (id: string) => {
    const base = allTypes ? [] : selectedTypes;
    const next = base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
    if (next.length === 0) {
      setAllTypes(true);
      setSelectedTypes([]);
      return;
    }
    setAllTypes(false);
    setSelectedTypes(next);
  };

  const createDelegation = async () => {
    if (!colleague) {
      setFormError('Choose a colleague.');
      return;
    }
    if (!allTypes && selectedTypes.length === 0) {
      setFormError('Choose at least one approval type.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await approvalsApi.createDelegation({
        toEmployeeId: colleague.value,
        workTypes: allTypes ? undefined : selectedTypes.join(','),
      });
      setColleague(null);
      setAllTypes(true);
      setSelectedTypes([]);
      await load();
    } catch (e: any) {
      setFormError(e?.response?.data?.message || 'Could not save this delegation.');
    } finally {
      setSaving(false);
    }
  };

  const activeDelegations = delegations.filter((d) => d.isActive);
  const showingAll = history?.scope === 'all' || requestedAll;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold text-neutral-800">{showingAll ? 'Delegations' : 'My Delegates'}</h1>
      <p className="text-sm text-neutral-500 mt-1">
        {showingAll
          ? 'Delegation history for everyone in this tenant: who it was delegated to, which approval types, what reached their plate, how it was handled, and the current status.'
          : (
            <>
              Hand approval to a colleague while you are away. Timecards stay on{' '}
              <button type="button" className="text-blue-600 hover:underline" onClick={() => navigate('/timecards')}>
                Timecards
              </button>
              . Other requests stay in{' '}
              <button type="button" className="text-blue-600 hover:underline" onClick={() => navigate('/inbox')}>
                Inbox
              </button>
              .
            </>
          )}
      </p>

      <div className="mt-5 flex gap-2 border-b border-neutral-200">
        {([
          ['current', 'Current'],
          ['history', 'History'],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${
              tab === id
                ? 'border-blue-600 text-blue-700 font-medium'
                : 'border-transparent text-neutral-500 hover:text-neutral-800'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
      {loading && tab === 'current' && <p className="mt-4 text-neutral-500">Loading…</p>}

      {tab === 'history' ? (
        <section className="mt-6">
          {!showingAll && (
            <>
              <h2 className="text-lg font-semibold text-neutral-800">Delegation history</h2>
              <p className="text-sm text-neutral-500 mt-1">
                Who it was delegated to, which approval types, what reached their plate, how it was handled, and the current status.
              </p>
            </>
          )}
          {historyError && (
            <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{historyError}</div>
          )}
          {historyLoading && <p className="mt-4 text-neutral-500">Loading history…</p>}
          {!historyLoading && history && history.delegations.length === 0 && (
            <p className="mt-4 text-sm text-neutral-500">No delegations yet.</p>
          )}
          <div className="mt-4 space-y-4">
            {history?.delegations.map((row) => (
              <article key={row.id} className="border border-neutral-200 rounded-lg bg-white">
                <div className="px-4 py-3 border-b border-neutral-100">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-medium text-neutral-900">
                      {row.fromName} → {row.toName}
                    </p>
                    <span className={`text-xs px-2 py-0.5 rounded ${row.isActive ? 'bg-green-50 text-green-800' : 'bg-neutral-100 text-neutral-600'}`}>
                      {row.isActive ? 'Active' : 'Ended'}
                    </span>
                  </div>
                  <p className="text-sm text-neutral-600 mt-1">{formatWorkTypes(row.workTypes)}</p>
                  <p className="text-xs text-neutral-500 mt-1">{periodLabel(row.effectiveFrom, row.effectiveTo, row.isActive)}</p>
                </div>
                {row.items.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-neutral-500">Nothing reached their plate during this delegation.</p>
                ) : (
                  <ul className="divide-y divide-neutral-100">
                    {row.items.map((item) => (
                      <li key={`${item.workType}-${item.entityId}`} className="px-4 py-3 text-sm">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <p className="font-medium text-neutral-900">
                            {item.workTypeLabel}
                            {item.subjectName ? ` · ${item.subjectName}` : ''}
                          </p>
                          <span className="text-xs text-neutral-600">{item.status}</span>
                        </div>
                        <p className="text-neutral-700 mt-0.5">{item.title}</p>
                        <p className="text-xs text-neutral-500 mt-1">
                          On their plate {formatCompanyDateTime(item.arrivedAt)}
                          {' · '}
                          {item.handled}
                          {item.handledAt ? ` · ${formatCompanyDateTime(item.handledAt)}` : ''}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            ))}
          </div>
        </section>
      ) : (
      <section className="mt-8">
        <h2 className="text-lg font-semibold text-neutral-800">Delegate authority</h2>
        <p className="text-sm text-neutral-500 mt-1">
          While you are away, a colleague can approve on your behalf. You can end this at any time.
        </p>

        <div className="mt-4 max-w-xl">
          <label className="block text-sm font-medium text-neutral-700 mb-1" htmlFor="delegate-colleague">
            Colleague
          </label>
          <Select<ColleagueOption, false>
            inputId="delegate-colleague"
            options={colleagueOptions}
            value={colleague}
            onChange={(opt: SingleValue<ColleagueOption>) => {
              setColleague(opt);
              setFormError(null);
            }}
            isClearable
            isDisabled={loading || saving}
            placeholder="Search by name…"
            noOptionsMessage={() => 'No matching colleagues'}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            styles={{
              control: (base, state) => ({
                ...base,
                minHeight: 38,
                fontSize: '0.875rem',
                borderColor: state.isFocused ? '#2563eb' : '#d4d4d4',
                boxShadow: state.isFocused ? '0 0 0 1px #2563eb' : 'none',
              }),
              menuPortal: (base) => ({ ...base, zIndex: 40 }),
              option: (base) => ({ ...base, fontSize: '0.875rem' }),
            }}
          />
        </div>

        <fieldset className="mt-4">
          <legend className="text-sm font-medium text-neutral-700">What they can approve</legend>
          <label className="mt-2 flex items-center gap-2 text-sm text-neutral-800">
            <input
              type="checkbox"
              checked={allTypes}
              onChange={() => {
                setAllTypes(true);
                setSelectedTypes([]);
              }}
            />
            All types
          </label>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-xl">
            {workTypes.map((type) => (
              <label key={type.id} className="flex items-center gap-2 text-sm text-neutral-800">
                <input
                  type="checkbox"
                  checked={!allTypes && selectedTypes.includes(type.id)}
                  onChange={() => toggleType(type.id)}
                />
                {type.label}
              </label>
            ))}
          </div>
        </fieldset>

        {formError && (
          <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 max-w-xl">
            {formError}
          </div>
        )}

        <button
          type="button"
          className="mt-4 px-3 py-1.5 bg-blue-600 text-white rounded text-sm disabled:opacity-50"
          disabled={saving || loading}
          onClick={() => void createDelegation()}
        >
          {saving ? 'Saving…' : 'Delegate'}
        </button>

        <ul className="mt-6 text-sm space-y-2">
          {activeDelegations.length === 0 && !loading && (
            <li className="text-neutral-500">No active delegation. Ended ones are on the History tab.</li>
          )}
          {activeDelegations.map((d) => (
            <li key={d.id} className="flex justify-between gap-4 border-b border-neutral-100 py-2">
              <span className="text-neutral-800">
                {d.fromName} → {d.toName} ({formatWorkTypes(d.workTypes)})
              </span>
              {d.fromEmployeeId === employee?.id && (
                <button
                  type="button"
                  className="text-red-600 text-xs shrink-0"
                  onClick={() => void approvalsApi.revokeDelegation(d.id).then(load)}
                >
                  End
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>
      )}
    </div>
  );
};

export default MyTeam;
